import React, { useState, useEffect } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import './App.css';

// Components
import {
  CustomCursor,
  DesktopIcon,
  DraggableWindow,
  Taskbar,
  StartMenu,
  BootScreen,
  ErrorPopup,
  StickyNote,
  StatusWidget,
  SnakeGame,
  TypingGame,
  AboutContent,
  ExperienceContent,
  ProjectsContent,
  ContactContent
} from './components';

// Data
import { DESKTOP_ICONS, ERROR_MESSAGES, ICONS } from './data/config';
import resumePdf from './assets/OssamaElhelali_SWE_2026.pdf';

// Window configurations
const WINDOWS = [
  { id: 'about', title: 'About Me', Icon: ICONS.about, content: <AboutContent />, defaultPos: { x: 'center', y: 80 }, defaultSize: { width: 520, height: 550 } },
  { id: 'experience', title: 'Experience', Icon: ICONS.experience, content: <ExperienceContent />, defaultPos: { x: 100, y: 60 }, defaultSize: { width: 600, height: 520 }, glitchy: true },
  { id: 'projects', title: 'Projects', Icon: ICONS.projects, content: <ProjectsContent />, defaultPos: { x: 150, y: 60 }, defaultSize: { width: 480, height: 400 } },
  { id: 'contact', title: 'Contact', Icon: ICONS.contact, content: <ContactContent />, defaultPos: { x: 200, y: 100 }, defaultSize: { width: 400, height: 350 } },
  { id: 'snake', title: 'Snake', Icon: ICONS.snake, content: <SnakeGame />, defaultPos: { x: 250, y: 50 }, defaultSize: { width: 340, height: 400 } },
  { id: 'typing', title: 'TypeSpeed', Icon: ICONS.typing, content: <TypingGame />, defaultPos: { x: 300, y: 80 }, defaultSize: { width: 400, height: 350 } },
];

function App() {
  const [booted, setBooted] = useState(false);
  const [openWindows, setOpenWindows] = useState(['about']);
  const [minimizedWindows, setMinimizedWindows] = useState([]);
  const [windowOrder, setWindowOrder] = useState(['about']);
  const [startOpen, setStartOpen] = useState(false);
  const [errors, setErrors] = useState([]);

  // Keyboard shortcuts (Ctrl/Cmd + 1-6 to open windows)
  useEffect(() => {
    if (!booted) return;

    const handleKeyDown = (e) => {
      if (e.metaKey || e.ctrlKey) {
        const num = parseInt(e.key);
        if (num >= 1 && num <= WINDOWS.length) {
          e.preventDefault();
          openWindow(WINDOWS[num - 1].id);
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [booted]);

  // Random error popups - only when idle
  useEffect(() => {
    if (!booted) return;

    let lastActivity = Date.now();
    let interval;

    const resetActivity = () => {
      lastActivity = Date.now();
    };

    const checkAndShowError = () => {
      const idleTime = Date.now() - lastActivity;
      const isIdle = idleTime > 30000; // 30 seconds of inactivity

      if (isIdle && Math.random() > 0.9 && errors.length < 1) {
        setErrors(prev => [...prev, {
          id: Date.now(),
          message: ERROR_MESSAGES[Math.floor(Math.random() * ERROR_MESSAGES.length)],
          position: {
            x: Math.random() * (window.innerWidth - 350) + 50,
            y: Math.random() * (window.innerHeight - 250) + 50
          }
        }]);
      }
    };

    window.addEventListener('mousemove', resetActivity);
    window.addEventListener('keydown', resetActivity);
    window.addEventListener('click', resetActivity);

    interval = setInterval(checkAndShowError, 25000);

    return () => {
      clearInterval(interval);
      window.removeEventListener('mousemove', resetActivity);
      window.removeEventListener('keydown', resetActivity);
      window.removeEventListener('click', resetActivity);
    };
  }, [booted, errors.length]);

  const downloadResume = () => {
    const link = document.createElement('a');
    link.href = resumePdf;
    link.download = 'OssamaElhelali_Resume.pdf';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const openResume = () => {
    window.open(resumePdf, '_blank');
  };

  const openWindow = (id) => {
    if (id === 'resume') {
      downloadResume();
      return;
    }
    if (!openWindows.includes(id)) {
      setOpenWindows(prev => [...prev, id]);
    }
    setMinimizedWindows(prev => prev.filter(w => w !== id));
    focusWindow(id);
    setStartOpen(false);
  };

  const closeWindow = (id) => {
    setOpenWindows(prev => prev.filter(w => w !== id));
    setMinimizedWindows(prev => prev.filter(w => w !== id));
    setWindowOrder(prev => prev.filter(w => w !== id));
  };

  const minimizeWindow = (id) => {
    if (!minimizedWindows.includes(id)) {
      setMinimizedWindows(prev => [...prev, id]);
    }
  };

  const focusWindow = (id) => {
    setWindowOrder(prev => [...prev.filter(w => w !== id), id]);
  };

  const removeError = (id) => {
    setErrors(prev => prev.filter(e => e.id !== id));
  };

  return (
    <div className="os-container">
      <CustomCursor />

      <AnimatePresence mode="wait">
        {!booted ? (
          <BootScreen key="boot" onComplete={() => setBooted(true)} />
        ) : (
          <motion.div
            key="desktop"
            className="desktop"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
          >
            <div className="crt-overlay" />

            <div className="desktop-icons">
              {DESKTOP_ICONS.map(icon => (
                <DesktopIcon
                  key={icon.id}
                  {...icon}
                  onDoubleClick={() => openWindow(icon.id)}
                />
              ))}
            </div>

            <StickyNote />
            <StatusWidget />

            <AnimatePresence>
              {WINDOWS.map(win => (
                <DraggableWindow
                  key={win.id}
                  {...win}
                  isOpen={openWindows.includes(win.id)}
                  isMinimized={minimizedWindows.includes(win.id)}
                  onClose={closeWindow}
                  onMinimize={minimizeWindow}
                  onFocus={focusWindow}
                  zIndex={windowOrder.indexOf(win.id) + 10}
                >
                  {win.content}
                </DraggableWindow>
              ))}
            </AnimatePresence>

            <AnimatePresence>
              {errors.map(err => (
                <ErrorPopup
                  key={err.id}
                  message={err.message}
                  position={err.position}
                  onClose={() => removeError(err.id)}
                />
              ))}
            </AnimatePresence>

            <StartMenu
              isOpen={startOpen}
              windows={WINDOWS}
              onItemClick={openWindow}
              onResumeDownload={downloadResume}
              onResumeOpen={openResume}
            />

            <Taskbar
              windows={WINDOWS}
              openWindows={openWindows}
              minimizedWindows={minimizedWindows}
              onWindowClick={openWindow}
              onStartClick={() => setStartOpen(!startOpen)}
              startOpen={startOpen}
            />
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

export default App;
